// Properties (DRAW_PLAN §10, the default dock): context-aware. With a selection: fill / stroke at a glance,
// the live shape's own parameters (corner radii, sides, points …) with Expand, and the Transform panel.
// Without: the document's units and colour mode.
import { useT } from '../../../i18n';
import { useDesign } from '../../state';
import { useDesignUi } from '../designUi';
import { TransformPanel } from './TransformPanel';
import { cmd, expandShapes, setColorMode, setShape } from '../../commands/commands';
import { findNode } from '../../model/tree';
import type { Shape, Unit } from '../../model/types';
import { PaintChip } from './AppearanceDock';
import { editLook, shownAppearance } from '../look';
import { fromPt, toPt } from '../../model/units';
import { defaultStroke } from '../../model/defaults';
import { useState } from 'react';
import { averageSelected, compound, joinPaths, reversePaths, simplifyPaths } from '../pathActions';

const UNITS: Unit[] = ['pt', 'px', 'mm', 'cm', 'in'];

export function PropertiesDock() {
  const t = useT();
  const sel = useDesignUi((st) => st.selection);
  useDesign((st) => st.version);
  const doc = useDesign((st) => st.doc)!;
  if (!sel.length)
    return (
      <div data-testid="doc-properties" style={{ display: 'grid', gap: 6 }}>
        <label>
          {t('units')}{' '}
          <select
            data-testid="doc-units"
            value={doc.units}
            onChange={(e) =>
              useDesign.getState().run(cmd('cmdUnits', (d) => void (d.units = e.target.value as Unit)))
            }
          >
            {UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('colorMode')}{' '}
          <select
            data-testid="doc-colormode"
            value={doc.colorMode}
            onChange={(e) => useDesign.getState().run(setColorMode(e.target.value as 'rgb' | 'cmyk'))}
          >
            <option value="rgb">RGB</option>
            <option value="cmyk">CMYK</option>
          </select>
        </label>
        {doc.colorMode === 'cmyk' && <p style={{ margin: 0, opacity: 0.7 }}>{t('cmykApprox')}</p>}
        <p style={{ opacity: 0.7, margin: 0 }}>{t('dockEmpty')}</p>
      </div>
    );
  const a = shownAppearance();
  const single = sel.length === 1 ? findNode(doc, sel[0]!)?.node : undefined;
  const shape = single?.kind === 'shape' ? single.shape : null;
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <section style={{ display: 'flex', gap: 8, alignItems: 'center' }} data-testid="quick-look">
        {t('fill')}
        <PaintChip
          testid="pp-fill"
          paint={a.fills[0]?.paint ?? { kind: 'none' }}
          onChange={(c) =>
            editLook((x) => {
              if (!c) x.fills = [];
              else if (x.fills[0]) x.fills[0].paint = { kind: 'solid', color: c };
              else x.fills.push({ paint: { kind: 'solid', color: c }, opacity: 1, blend: 'normal' });
            })
          }
        />
        {t('stroke')}
        <PaintChip
          testid="pp-stroke"
          paint={a.strokes[0]?.paint ?? { kind: 'none' }}
          onChange={(c) =>
            editLook((x) => {
              if (!c) x.strokes = [];
              else if (x.strokes[0]) x.strokes[0].paint = { kind: 'solid', color: c };
              else x.strokes.push({ ...defaultStroke(), paint: { kind: 'solid', color: c } });
            })
          }
        />
      </section>
      {shape && single && <ShapeParams id={single.id} shape={shape} />}
      <PathSection />
      <TransformPanel />
    </div>
  );
}

function ShapeParams({ id, shape }: { id: string; shape: Shape }) {
  const t = useT();
  const doc = useDesign((st) => st.doc)!;
  const u = doc.units,
    dpi = doc.rasterDpi;
  const len = (
    label: string,
    testid: string,
    v: number,
    set: (s: Shape, v: number) => void,
    unitless = false,
  ) => (
    <label key={testid} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <span style={{ width: 90 }}>{label}</span>
      <input
        type="number"
        step="any"
        data-testid={testid}
        key={`${testid}:${v}`}
        defaultValue={unitless ? v : +fromPt(v, u, dpi).toFixed(4)}
        style={{ width: 80 }}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        onBlur={(e) => {
          const n = Number(e.target.value);
          if (!Number.isFinite(n)) return;
          const pt = unitless ? n : toPt(n, u, dpi);
          if (Math.abs(pt - v) > 1e-9) useDesign.getState().run(setShape(id, (s) => set(s, pt)));
        }}
      />
    </label>
  );
  return (
    <section data-testid="shape-params" style={{ display: 'grid', gap: 4 }}>
      <strong>{t('shapeOptions')}</strong>
      {shape.type === 'rect' &&
        (['tl', 'tr', 'br', 'bl'] as const).map((c, i) =>
          len(t(`radius_${c}`), `radius-${c}`, shape.radii[i]!, (s, v) => {
            if (s.type === 'rect') s.radii[i] = Math.max(0, v);
          }),
        )}
      {shape.type === 'ellipse' && (
        <>
          {len(
            t('radiusX'),
            'ellipse-rx',
            shape.rx,
            (s, v) => s.type === 'ellipse' && ((s.rx = Math.abs(v)), (s.cx = Math.abs(v))),
          )}
          {len(
            t('radiusY'),
            'ellipse-ry',
            shape.ry,
            (s, v) => s.type === 'ellipse' && ((s.ry = Math.abs(v)), (s.cy = Math.abs(v))),
          )}
        </>
      )}
      {shape.type === 'polygon' && (
        <>
          {len(
            t('sides'),
            'polygon-sides',
            shape.sides,
            (s, v) => s.type === 'polygon' && (s.sides = Math.max(3, Math.min(64, Math.round(v)))),
            true,
          )}
          {len(t('radius'), 'polygon-r', shape.r, (s, v) => s.type === 'polygon' && (s.r = Math.abs(v)))}
        </>
      )}
      {shape.type === 'star' && (
        <>
          {len(
            t('points'),
            'star-points',
            shape.points,
            (s, v) => s.type === 'star' && (s.points = Math.max(3, Math.min(64, Math.round(v)))),
            true,
          )}
          {len(t('outerRadius'), 'star-r1', shape.r1, (s, v) => s.type === 'star' && (s.r1 = Math.abs(v)))}
          {len(t('innerRadius'), 'star-r2', shape.r2, (s, v) => s.type === 'star' && (s.r2 = Math.abs(v)))}
        </>
      )}
      <button data-testid="expand-shape" onClick={() => useDesign.getState().run(expandShapes([id]))}>
        {t('expandShape')}
      </button>
    </section>
  );
}

/** Path commands (D3): shown for every selection that holds paths or shapes. */
function PathSection() {
  const t = useT();
  const [tol, setTol] = useState(1);
  return (
    <section data-testid="path-section" style={{ display: 'grid', gap: 4 }}>
      <strong>{t('pathOps')}</strong>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        <button data-testid="path-join" onClick={joinPaths} title="Ctrl+J">
          {t('pathJoin')}
        </button>
        <button data-testid="path-average" onClick={averageSelected} title="Ctrl+Alt+J">
          {t('pathAverage')}
        </button>
        <button data-testid="path-compound" onClick={() => compound(true)} title="Ctrl+8">
          {t('pathCompound')}
        </button>
        <button data-testid="path-release" onClick={() => compound(false)} title="Ctrl+Alt+Shift+8">
          {t('pathRelease')}
        </button>
        <button data-testid="path-reverse" onClick={reversePaths}>
          {t('pathReverse')}
        </button>
      </div>
      <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        {t('pathSimplify')}
        <input
          type="number"
          min={0.05}
          step={0.05}
          value={tol}
          data-testid="simplify-tol"
          onChange={(e) => setTol(Number(e.target.value) || 1)}
          style={{ width: 60 }}
        />
        pt
        <button data-testid="path-simplify" onClick={() => simplifyPaths(tol)}>
          {t('apply')}
        </button>
      </label>
    </section>
  );
}
